import * as vp from 'vopay';
import {
  Box,
  capitalizeFirstLetterOfWords,
  GetColorStatus,
  formattedCurrency,
  Tooltip,
  MaterialSymbol,
  Pagination,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { format, subWeeks } from 'date-fns';
import { Button, TableFiltersContainer, TransactionStatus, Table, Title } from 'UI';
import { FunctionComponent, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Truncate from '@/components/UI/Truncate';
import CopyButton from '@/components/UI/CopyButton/CopyButton';
import TransactionsSearchForm from './TransactionsSearchForm';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import styles from './TransactionsList.module.scss';
import ViewListIcon from './view_list.svg';

type TransactionListProps = {
  areDatesValid: boolean;
  transactions: vp.AccountTransactionsMap;
  isExportingCSV: boolean;
  refundSubmissions: { [transactionId: string]: boolean };
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  pageCount: number;
  pageIndex: number;
  accountName: string;
  handlers: {
    onTransactionsSearch: (values: vp.AccountTransactionsRequest) => void;
    onTransactionRefund: (transactionId: string) => void;
    handleDate: (values: [vp.Timestamp, vp.Timestamp] | undefined) => void;
    exportCSV: () => void;
    handlePageChange: (selectedPage: number) => void;
  };
  setShouldShowEndDateError?: (event: boolean) => void;
};

const Transactions: FunctionComponent<TransactionListProps> = ({
  transactions,
  handlers,
  areDatesValid,
  hasInvalidEndDate,
  shouldShowEndDateError,
  setShouldShowEndDateError,
  pageCount,
  pageIndex,
  accountName,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Transaction' });
  const { canRefundTransactions } = useModuleFeatureFlag('accounts');
  const { accountId } = useParams<{ accountId: string }>();
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>();
  const [filtersApplied, setFiltersApplied] = useState(false);

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const [searchValues, setSearchValues] = useState<vp.AccountTransactionsRequest>({
    StartDateTime: format(subWeeks(new Date(), 1), 'yyyy-MM-dd'),
    EndDateTime: format(new Date(), 'yyyy-MM-dd'),
    Currency: 'CAD',
    TransactionType: undefined,
    IsFlagged: undefined,
    TransactionStatus: undefined,
    SettlementDate: undefined,
    SearchText: '',
    IsRefunded: false,
  });

  const memoizedSearchValues = useMemo(() => searchValues, [searchValues]);

  const refundColumn: vp.ColConfig<vp.Transaction, string> | null = canRefundTransactions
    ? {
        id: 'refund',
        header: t('Table.refund', { defaultValue: 'Refund' }),
        align: 'left' as const,
        template: ({ data }) =>
          shouldOfferRefund(data) ? (
            <Button
              id={`transaction-refund-${data.TransactionID}`}
              size="small"
              disabled={isRoleViewOnly}
              onClick={() => handlers.onTransactionRefund(data.TransactionID)}
            >
              {t('Table.refund', { defaultValue: 'Refund' })}
            </Button>
          ) : (
            ''
          ),
      }
    : null;

  const tableProps: vp.TableProps<vp.Transaction> = {
    id: 'transaction-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'transactionDate',
        header: t('Table.transactionDate', { defaultValue: 'Trans. Date' }),
        align: 'left',

        template: (transaction) => {
          return (
            <Box gap="sm" ai="center">
              {transaction.data.TransactionFlag !== '' && (
                <Tooltip
                  information={transaction.data.TransactionFlag}
                  hasButton={false}
                  customElement={<MaterialSymbol fillAmount={1} icon="flag" size="24px" color="rgba(189, 58, 45, 1)" />}
                  position="right"
                />
              )}

              <span>{transaction.data.TransactionDateTime}</span>
            </Box>
          );
        },
      },
      {
        id: 'id',
        header: t('Table.transactionID', { defaultValue: 'Trans. ID' }),
        align: 'left',

        template: ({ data }) =>
          shouldShowView(data) ? (
            <Box gap="sm" ai="center">
              <Table.Link
                to={{
                  pathname: `/accounts/${accountId}/${removeWhiteSpace(data)}/transaction/${data.TransactionID}`,
                  search: new URLSearchParams({
                    type: data.TransactionType,
                    accountName,
                  }).toString(),
                }}
              >
                {data.TransactionID}
              </Table.Link>

              <CopyButton content={data.TransactionID} />
            </Box>
          ) : (
            <>{data.TransactionID}</>
          ),
      },
      {
        id: 'name',
        header: t('Table.name', { defaultValue: 'Name' }),
        align: 'left',

        field: 'FullName',
      },
      {
        id: 'type',
        header: t('Table.type', { defaultValue: 'Type' }),
        align: 'left',

        field: 'TransactionType',
      },
      {
        id: 'paymentRailID',
        header: t('Table.paymentRailID', { defaultValue: 'Payment Rail ID' }),
        align: 'left',

        field: 'PaymentRailID',
      },
      {
        id: 'referenceNumber',
        header: t('Table.referenceNumber', { defaultValue: 'Ref. Number' }),
        align: 'left',

        field: 'ClientReferenceNumber',
      },
      {
        id: 'credit',
        header: t('Table.credit', { defaultValue: 'Credit' }),
        align: 'left',
        style: {
          minWidth: '100px',
        },
        template: (transaction) => {
          return (
            <>
              {transaction.data.CreditAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`$${formattedCurrency(transaction.data.CreditAmount)}`}</span>

                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('Table.pending', { defaultValue: 'Pending' })}{' '}
                      {`$${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'debit',
        header: t('Table.debit', { defaultValue: 'Debit' }),
        align: 'left',

        template: (transaction) => {
          return (
            <>
              {transaction.data.DebitAmount !== '0.00' && (
                <Box flx="col" gapSize="6px">
                  <span>{`$${formattedCurrency(transaction.data.DebitAmount)}`}</span>

                  {transaction.data.HoldAmount !== '0.00' && (
                    <span className={styles.pendingAmount}>
                      {t('Table.pending', { defaultValue: 'Pending' })}{' '}
                      {`$${formattedCurrency(transaction.data.HoldAmount)}`}
                    </span>
                  )}
                </Box>
              )}
            </>
          );
        },
      },
      {
        id: 'status',
        header: t('Table.status', { defaultValue: 'Status' }),
        align: 'left',

        template: ({ data }) => (
          <TransactionStatus
            label={capitalizeFirstLetterOfWords(data.TransactionStatus || '')}
            status={GetColorStatus(data.TransactionStatus)}
          />
        ),
      },
      {
        id: 'settlementDate',
        header: t('Table.settlementDate', { defaultValue: 'Settlement Date' }),
        align: 'left',

        field: 'SettlementDate',
      },
      {
        id: 'notes',
        header: t('Table.notes', { defaultValue: 'Notes' }),
        align: 'left',
        style: {
          maxWidth: '300px',
        },
        template: (transaction) => {
          const isTruncate = transaction.data.Notes.length > 25;

          return (
            <Tooltip
              information={transaction.data.Notes}
              isDisabled={!isTruncate}
              hasButton={false}
              customElement={
                <Box gap="sm" ai="center" jc="sb" className="notes">
                  <Truncate text={transaction.data.Notes} maxLength={25} truncateEnding="..." />
                  {transaction.data.Notes && <CopyButton content={transaction.data.Notes} />}
                </Box>
              }
              position="bottomLeft"
            />
          );
        },
      },
      ...(refundColumn ? [refundColumn] : []),
    ],
    rows:
      transactions[accountId]
        ?.filter((transaction) => !transaction.ConfirmedByClient)
        .map((transaction) => ({
          id: transaction.TransactionID,
          data: transaction,
        })) || [],
  };

  const search = (values: vp.AccountTransactionsRequest) => {
    setSearchValues(values);
    handlers.onTransactionsSearch(values);
    const fromDate = timeRange?.[0] != null ? format(new Date(timeRange[0]), 'yyyy-MM-dd') : '';
    const toDate = timeRange?.[1] != null ? format(new Date(timeRange[1]), 'yyyy-MM-dd') : '';

    setFiltersApplied(
      !!values.Currency ||
        !!values.TransactionType ||
        !!values.TransactionStatus ||
        !!values.SettlementDate ||
        !!values.SearchText ||
        fromDate !== format(subWeeks(new Date(), 1), 'yyyy-MM-dd') ||
        toDate !== format(new Date(), 'yyyy-MM-dd'),
    );
  };

  return (
    <div className={styles.container}>
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <div className={styles.trigger}>
          <TableFiltersContainer.Trigger />

          <Button
            id="accounts-ExportCSV"
            onClick={() => {
              handlers.exportCSV();
            }}
            size="small"
            disabled={!areDatesValid || isRoleViewOnly}
          >
            {t('exportCSVButton', { defaultValue: 'Export CSV' })}
          </Button>
        </div>

        <TableFiltersContainer.Content>
          <TransactionsSearchForm
            handlers={handlers}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            search={search}
            defaultValues={memoizedSearchValues}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div className={styles.tableContainer}>
        {tableProps.rows.length === 0 ? (
          <div className={styles.emptyState}>
            <img src={ViewListIcon} className={styles.emptyIcon} alt="" />

            <Box flx="col" ai="center" gapSize="10px">
              <Title level="4">{t('emptyState.title', { defaultValue: 'No transactions available' })}</Title>

              <p className={styles.emptyDescription}>
                {t('emptyState.description', {
                  defaultValue: 'Transactions associated with this account will appear here.',
                })}
              </p>
            </Box>
          </div>
        ) : (
          <>
            <div className={styles.tableScroll}>
              <Table {...tableProps} />
            </div>

            <Box flx="row" jc="center" mt="md" base="8">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => {
                    handlers.handlePageChange(e.selected);
                  }}
                />
              )}
            </Box>
          </>
        )}
      </div>
    </div>
  );

  function shouldOfferRefund({ IsRefunded, TransactionStatus, TransactionType }: vp.Transaction) {
    const isComplete = TransactionStatus === vp.TransactionStatus.SUCCESSFUL;
    const isEFT = [vp.TransactionType.EFT_FUNDING, vp.TransactionType.EFT_WITHDRAWAL].includes(TransactionType);

    return IsRefunded !== '1' && isComplete && isEFT;
  }

  function shouldShowView({ TransactionType }: vp.Transaction) {
    const isEFT = [vp.TransactionType.EFT_FUNDING, vp.TransactionType.EFT_WITHDRAWAL].includes(TransactionType);

    const isInteract = [vp.TransactionType.INTERACT_MONEY_REQUEST, vp.TransactionType.INTERACT_BULK_PAYOUT].includes(
      TransactionType,
    );

    const isCreditCard = [vp.TransactionType.CREDIT_CARD].includes(TransactionType);

    return isEFT || isInteract || isCreditCard;
  }

  function removeWhiteSpace({ TransactionType }: vp.Transaction) {
    return decodeURI(TransactionType).replaceAll(' ', '');
  }
};

export default Transactions;
